import { InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axiosInstance from "../../shared/axiosInstance";
import { API, isSuperAdminRole } from "../../constants";
import {
  IFinanceAlert,
  IFinanceAlertPage,
  IFinanceAlertSummary,
} from "./financeAlerts.types";

export const financeAlertKeys = {
  all: ["financeAlerts"] as const,
  list: (recipientId: string) =>
    [...financeAlertKeys.all, "list", recipientId] as const,
  summary: (recipientId: string) =>
    [...financeAlertKeys.all, "summary", recipientId] as const,
};

export type TFinanceAlertPages = InfiniteData<IFinanceAlertPage>;

export const getFinanceAlertRecipientId = (userData: any): string | null => {
  if (!userData) return null;
  if (isSuperAdminRole(userData["custom:roleName"])) {
    return API.FINANCE_ALERTS.SUPER_ADMIN_RECIPIENT_ID;
  }
  return userData["custom:vendorId"] ?? userData.sub ?? null;
};

export const fetchFinanceAlertPage = async (
  recipientId: string,
  cursor?: string
): Promise<IFinanceAlertPage> => {
  const query = new URLSearchParams({
    notificationCreatedByUserId: recipientId,
    limit: String(API.FINANCE_ALERTS.PAGE_SIZE),
  });
  if (cursor) query.set("cursor", cursor);
  const res = await axiosInstance.get(
    `${process.env.REACT_APP_BASE_URL}/${API.FINANCE_ALERTS.LIST}?${query.toString()}`
  );
  return res.data;
};

export const fetchFinanceAlertSummary = async (
  recipientId: string
): Promise<IFinanceAlertSummary> => {
  const query = new URLSearchParams({ notificationCreatedByUserId: recipientId });
  const res = await axiosInstance.get(
    `${process.env.REACT_APP_BASE_URL}/${API.FINANCE_ALERTS.SUMMARY}?${query.toString()}`
  );
  return res.data?.data;
};

export const useFinanceAlertsQuery = (recipientId: string | null) =>
  useInfiniteQuery({
    queryKey: financeAlertKeys.list(recipientId ?? ""),
    queryFn: ({ pageParam }) =>
      fetchFinanceAlertPage(recipientId as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.cursor ? JSON.stringify(lastPage.cursor) : undefined,
    enabled: !!recipientId,
    staleTime: Infinity,
  });

export const useFinanceAlertSummaryQuery = (recipientId: string | null) =>
  useQuery({
    queryKey: financeAlertKeys.summary(recipientId ?? ""),
    queryFn: () => fetchFinanceAlertSummary(recipientId as string),
    enabled: !!recipientId,
    refetchInterval: API.FINANCE_ALERTS.POLL_INTERVAL_MS,
  });

export const flattenFinanceAlerts = (
  data: TFinanceAlertPages | undefined
): IFinanceAlert[] => data?.pages.flatMap((page) => page?.data ?? []) ?? [];
